/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import GroupProperties, {getAssetCategories} from '@ion/core/src/services/GroupProperties';
import {FilterOption, FilterPropsType} from '../types/types';
import Filter from './Filter';
import {createFilterOptions, getMenuStyles} from './FilterHelpers';
import {FilterParamsProps} from './useFilterSubmit';

/**
 * Defines the prop definitions for AssetCategoryFilter ...
 *
 * accountId:           The ID of currently selected account.
 * loadAssetCategories: Override function to load the default options data.
 */
export interface AssetCategoryFilterProps extends FilterPropsType {
  accountId: number;
  loadAssetCategories?: Function;
};

/**
 * The AssetCategoryFilter component is a reusable filter for managing the filtering
 * of asset categories. It is intended to be used in mutiple pages and be a responsive.
 *
 * ```typescript
 *
 * import AssetCategoryFilter from '@ion/dls/src/filters';
 *
 * <AssetCategoryFilter elementId={elementId}
 *   name={name}
 *   accountId={accountId}
 *   loadAssetCategories={loadAssetCategories}
 *   filterState={filterState}
 *   showSelectedOptionsCount={false}
 *   primaryButtonDisable={primaryButtonDisabled}
 *   secondaryButtonDisable={secondaryButtonDisabled}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 *   onMenuClose={onMenuClose} />
 *
 * ```
 */
export const AssetCategoryFilter = ({
  elementId,
  name,
  accountId,
  loadAssetCategories,
  primaryButtonDisabled,
  secondaryButtonDisabled,
  onSubmit,
  onCancel,
  onMenuClose,
  filterState = {},
  showSelectedOptionsCount = true,
  isMobileView = false,
  forceRefresh
}: AssetCategoryFilterProps) => {
  const [refresh, setRefresh] = useState<boolean>(false);
  const {
    loading: isLoading,
    error: hasErrors,
    selected: selectedOptions
  }                                         = filterState;
  const {
    menuLeft,
    menuWidth,
    contentGridTemplateColumns,
    contentJustifyContent,
    contentGridColumnGap,
    contentGridRowGap
  } = getMenuStyles(isMobileView);

  function handleSubmit({selections, options, loading, hasError}) {
    const filterParams: FilterParamsProps = {
      optionsKey    : FilterOption.value,
      selectionsKey : FilterOption.value,
      options,
      selections,
      loading,
      hasError
    };
    onSubmit?.(name, filterParams);
  };

  const loadFilterOptions = useCallback(async function(accountId) {
    !loadAssetCategories && (await GroupProperties.getInheritedProperties(accountId));
    const categories = await (loadAssetCategories || getAssetCategories)();
    return {
      result       : categories,
      filterResult : createFilterOptions(categories)
    };
  }, [accountId, getAssetCategories]);

  useEffect(() => {
    setRefresh(true);
  }, [accountId]);

  useEffect(function() {
    if (forceRefresh && !refresh) {
      setRefresh(true);
    }
  }, [forceRefresh]);

  const updateRefresh = useCallback(function() {
    setRefresh(!refresh);
  }, [refresh]);

  return (
    <Filter optionType={translate('common:filters.assetCategory')}
      hasErrors={Boolean(hasErrors)}
      optionPropId={FilterOption?.label}
      optionPropLabel={FilterOption?.value}
      selectedOptionIds={selectedOptions ?? []}
      loading={isLoading}
      showSelectedOptionsCount={showSelectedOptionsCount}
      primaryButtonDisabled={primaryButtonDisabled}
      secondaryButtonDisabled={secondaryButtonDisabled}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      onMenuClose={onMenuClose}
      elementId={elementId}
      menuLeft={menuLeft}
      menuWidth={menuWidth}
      contentGridTemplateColumns={contentGridTemplateColumns}
      contentJustifyContent={contentJustifyContent}
      contentGridColumnGap={contentGridColumnGap}
      contentGridRowGap={contentGridRowGap}
      loadFilterOptions={loadFilterOptions}
      accountId={accountId}
      isMobileView={isMobileView}
      refresh={refresh}
      updateRefresh={updateRefresh} />
  );
};

export default AssetCategoryFilter;
